import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary component
 *
 * Catches JavaScript errors in child components, logs errors, and displays a fallback UI.
 * Prevents the entire app from crashing when a component throws an error.
 *
 * @param children - Child components to wrap
 * @param fallback - Optional custom fallback UI to display on error
 * @param onError - Optional callback to handle errors (e.g., send to error tracking service)
 * @param showDetails - Whether to show error details in development (defaults to true in dev)
 *
 * @example
 * // Basic usage
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 *
 * @example
 * // With custom fallback
 * <ErrorBoundary fallback={<CustomErrorPage />}>
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * @example
 * // With error tracking
 * <ErrorBoundary onError={(error, errorInfo) => {
 *   trackError({ error, errorInfo });
 * }}>
 *   <MyComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Update state with error info
    this.setState({
      errorInfo,
    });

    // Call optional error handler (e.g., for error tracking services)
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // If custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Otherwise, render default error UI
      const { error, errorInfo } = this.state;
      const showDetails = this.props.showDetails ?? import.meta.env.DEV;

      return (
        <div className="flex items-center justify-center min-h-screen bg-background-primary px-4">
          <div className="max-w-2xl w-full">
            <div className="card p-8 text-center">
              {/* Error Icon */}
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-error-100 dark:bg-error-900">
                  <svg
                    className="w-8 h-8 text-error-600 dark:text-error-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              </div>

              {/* Error Title */}
              <h1 className="text-2xl font-bold text-text-primary mb-2">
                Oops! Something Went Wrong
              </h1>

              {/* Error Description */}
              <p className="text-text-secondary mb-6">
                We encountered an unexpected error. Please try refreshing the page or contact support
                if the problem persists.
              </p>

              {/* Action Buttons */}
              <div className="flex gap-4 justify-center mb-6">
                <button
                  onClick={this.handleReset}
                  className="btn-secondary"
                  type="button"
                >
                  Try Again
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="btn-primary"
                  type="button"
                >
                  Refresh Page
                </button>
              </div>

              {/* Error Details (Development Only) */}
              {showDetails && error && (
                <details className="mt-8 text-left">
                  <summary className="cursor-pointer text-sm font-medium text-text-secondary hover:text-text-primary mb-2">
                    Show Error Details
                  </summary>
                  <div className="bg-background-secondary rounded-lg p-4 overflow-auto max-h-96">
                    {/* Error Message */}
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-error-600 dark:text-error-400 mb-1">
                        Error Message:
                      </h3>
                      <p className="text-xs font-mono text-text-primary break-words">
                        {error.toString()}
                      </p>
                    </div>

                    {/* Component Stack */}
                    {errorInfo && errorInfo.componentStack && (
                      <div>
                        <h3 className="text-sm font-semibold text-error-600 dark:text-error-400 mb-1">
                          Component Stack:
                        </h3>
                        <pre className="text-xs font-mono text-text-secondary whitespace-pre-wrap break-words">
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
